import { blockerFunction } from "@src/utility_functions/utility_functions/dom_functions";
import { validateFileInterval } from "@src/utility_functions/inputValidation/validateFileInterval";
import { getConnector } from "#shared/connectedApps/registry.js";
import { deriveSourceKeyLinkedIn } from "#shared/connectedApps/sourceKeys.js";

// LinkedIn data-export adapter: Positions.csv → interval objects.
// Columns: Company Name, Title, Description, Location, Started On, Finished On.
// Dates are month-granular ("Jan 2020", sometimes bare "2020").

const DESCRIPTION_MAX = 9500;

const MONTHS = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

// "Jan 2020" | "2020" → { iso: midnight-UTC month/year start, granularity },
// else null. An explicit month map — new Date("Jan 2020") is locale/engine-
// dependent and can shift a job by a month at negative UTC offsets.
const parseLinkedInDateParts = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  const monthMatch = /^([A-Za-z]{3})\s+(\d{4})$/.exec(trimmed);
  if (monthMatch) {
    const monthIndex = MONTHS[monthMatch[1].toLowerCase()];
    if (monthIndex === undefined) return null;
    return {
      iso: new Date(
        Date.UTC(Number(monthMatch[2]), monthIndex, 1),
      ).toISOString(),
      granularity: "month",
    };
  }
  const yearMatch = /^(\d{4})$/.exec(trimmed);
  if (yearMatch) {
    return {
      iso: new Date(Date.UTC(Number(yearMatch[1]), 0, 1)).toISOString(),
      granularity: "year",
    };
  }
  return null;
};

export const parseLinkedInDate = (value) =>
  parseLinkedInDateParts(value)?.iso ?? null;

// Granularity is INCLUSIVE on the finish side: "Finished On: Mar 2021" ran
// through March (ends at April 1) and a bare "Finished On: 2020" ran through
// December 2020 (ends at Jan 1 2021 — treating it as January would put the
// end BEFORE a mid-year start and get the row falsely rejected). Also
// guarantees start < end for one-month/one-year positions (validators
// require strictly before).
export const computeLinkedInEndTime = (finishedOnParts) => {
  const date = new Date(finishedOnParts.iso);
  if (finishedOnParts.granularity === "year") {
    return new Date(Date.UTC(date.getUTCFullYear() + 1, 0, 1)).toISOString();
  }
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1),
  ).toISOString();
};

export const parseCSVDataIntoIntervalsLinkedIn = async (
  data,
  setParsedItemCount,
  setFileError,
) => {
  const connector = getConnector("linkedin");
  let returnValue = {
    errors: 0,
    intervals: [],
  };

  for (let index = 0; index < data.length; index++) {
    setParsedItemCount((prev) => {
      return prev + 1;
    });
    const item = data[index];
    const companyName = String(item["Company Name"] ?? "").trim();
    const title = String(item["Title"] ?? "").trim();
    const location = String(item["Location"] ?? "").trim();
    const positionDescription = String(item["Description"] ?? "").trim();
    if (!companyName && !title) {
      // papaparse commonly yields a blank trailing row — not an error
      continue;
    }
    const startTime = parseLinkedInDate(item["Started On"]);
    if (!startTime) {
      console.warn("LinkedIn position " + (index + 1) + ": unparseable date");
      returnValue.errors += 1;
      continue;
    }
    const finishedOnParts = parseLinkedInDateParts(item["Finished On"]);
    // Empty Finished On = current job → runs to the import moment. Known
    // limitation: dedup skips re-imports, so this endTime won't refresh.
    const current = !finishedOnParts;
    const endTime = current
      ? new Date().toISOString()
      : computeLinkedInEndTime(finishedOnParts);

    const metadata = { companyName };
    if (location) metadata.location = location;
    if (current) metadata.current = true;

    const newInterval = {
      label: title || companyName,
      description: [companyName, location, positionDescription]
        .filter(Boolean)
        .join(" · ")
        .slice(0, DESCRIPTION_MAX),
      startTime,
      endTime,
      primaryColor: connector.rowDefaults.primaryColor,
      secondaryColor: connector.rowDefaults.secondaryColor,
      source: connector.id,
      type: connector.intervalType,
      metadata,
      sourceKey: deriveSourceKeyLinkedIn({
        companyName,
        title,
        startedOnISO: startTime,
      }),
    };
    const intervalValid = validateFileInterval(
      newInterval,
      setFileError,
      index,
    );
    await blockerFunction(1);
    if (intervalValid) {
      returnValue.intervals.push(newInterval);
    } else {
      returnValue.errors += 1;
    }
  }
  return returnValue;
};
